import { StripeUseCheckoutElementsResult } from "@stripe/react-stripe-js/checkout";
import { Appearance, PaymentMethodResult, Stripe, StripeElements } from "@stripe/stripe-js";
import { GlobalStateController } from "bai-react-global-state";
import { User as FirebaseUser } from "firebase/auth";
import _ from "lodash";
import React, { forwardRef, useEffect } from "react";
import type StripeTypes from "stripe";

import { InternalServerError, PaymentRequiredError } from "apis-v2/errors";
import {
    ActionPermissionsObject,
    IUserStorageModel,
    IWorkspaceMetadata,
    IWorkspaceUserExtended,
    IWorkspaceUserInvite,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceUserRole
} from "common/interfaces";
import stripeProducts from "common/stripeProducts";
import getObjectHash from "common/utils/getObjectHash";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import * as StripeUtils from "common/utils/stripeUtils";
import { MAX_QUANTITIES } from "common/utils/workspaces/plans";
import Analytics from "js/analytics";
import type { GetWorkspaceStripeDataResponse } from "js/controllers/BillingController";
import { calculateTaxAmount, createCheckoutSession, ensureCustomerAndPaymentMethod, getPromotionCode, getWorkspaceStripeData, handleSuccessfulWorkspacePlanUpgrade, StripeRadarError, stripeRadarPaymentCheck, updateCheckoutSession, upgradeWorkspacePlan } from "js/controllers/BillingController";
import { getActionPermissionsForWorkspace, getInitialWorkspaceId, getWorkspaceUserInvites, getWorkspaceUsers } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { User } from "js/core/models/user";
import { handleStripeErrors } from "js/core/services/stripe";
import ux from "js/core/services/userExperiences";
import { trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import Spinner from "js/react/components/Spinner";
import withCheckoutSession from "js/react/views/Billing/utils/withCheckoutSession";
import withElements from "js/react/views/Billing/utils/withElements";
import withStripe from "js/react/views/Billing/utils/withStripe";

//////////////////////////////////////////////////////////////////
//////////////////// IMPORTANT NOTES /////////////////////////////
// Avoid adding any post or pre-checkout logic here             //
// If you need to do something post-checkout, like redirect     //
// to a specific page, or show a follow-up dialog, then do it   //
// on the higher level                                          //
//////////////////////////////////////////////////////////////////
// Checkout only supports upgrading existing workspace, it does //
// not support implicit workspace creation anymore              //
//////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////

const logger = getLogger(LogGroup.CHECKOUT);

const PROMOTION_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;
const MAX_PROMOTION_CODE_LENGTH = 64;

type BillingInterval = "month" | "year";
type BillingIntervalPriceIds = Partial<Record<BillingInterval, string>>;

export class CheckoutSessionConfirmationError extends Error {
    public readonly name = "CheckoutSessionConfirmationError";

    constructor(message: string, public readonly code: string) {
        super(message);
    }
}

export class CheckoutSessionFinalizationTimeoutError extends Error {
    public readonly name = "CheckoutSessionFinalizationTimeoutError";

    constructor(message: string) {
        super(message);
    }
}

export class AlreadyPayingForPlanError extends Error {
    public readonly name = "AlreadyPayingForPlanError";

    constructor(message: string) {
        super(message);
    }
}

export function normalizePromoCode(code: string) {
    if (typeof code !== "string") {
        return null;
    }

    const normalizedPromotionCode = code.trim();
    return normalizedPromotionCode === "" ? null : normalizedPromotionCode;
}

export function validatePromoCode(code: string) {
    code = normalizePromoCode(code);

    if (!code) {
        return "Promotion code is required";
    }

    if (code.length > MAX_PROMOTION_CODE_LENGTH) {
        return `Promotion code must be ${MAX_PROMOTION_CODE_LENGTH} characters or less`;
    }

    if (!PROMOTION_CODE_PATTERN.test(code)) {
        return "Promotion code contains invalid characters";
    }

    return null;
}

function getCheckoutPropsFromLocation() {
    const searchParams = new URLSearchParams(window.location.search);

    let promotionCode = normalizePromoCode(searchParams.get("coupon") ?? searchParams.get("promotionCode"));
    if (validatePromoCode(promotionCode)) {
        promotionCode = null;
    }

    const priceIds = searchParams.getAll("price")
        .flatMap(value => value.split(" "))
        .map(id => id?.trim())
        .filter(Boolean);
    const quantity = parseInt(searchParams.get("quantity") ?? "", 10);
    const minQuantity = Number.isFinite(quantity) ? quantity : 1;
    let billingInterval = searchParams.get("billingTerm") as "month" | "year" | null;
    if (billingInterval && !["month", "year"].includes(billingInterval)) {
        billingInterval = null;
    }
    let tier = searchParams.get("plan") as WorkspacePlanTier | null;
    if (tier && !Object.values(WorkspacePlanTier).includes(tier)) {
        tier = null;
    }

    const checkoutProps = {
        ...(tier ? { tier } : {}),
        ...(billingInterval ? { billingInterval } : {}),
        ...(searchParams.has("displayName") ? { displayName: searchParams.get("displayName") } : {}),
        ...(promotionCode ? { promotionCode } : {}),
        ...(searchParams.has("price") ? { priceIds } : {}),
        ...(searchParams.has("quantity") ? { minQuantity } : {}),
        ...(searchParams.has("workspaceId") ? { workspaceId: searchParams.get("workspaceId") } : {})
    };

    if (!checkoutProps.tier && checkoutProps.displayName) {
        const planAssumedFromDisplayName = checkoutProps.displayName.toLowerCase().includes("pro") ? WorkspacePlanTier.PRO : WorkspacePlanTier.TEAM;
        checkoutProps.tier = planAssumedFromDisplayName;
    }

    return checkoutProps;
}

async function getInitialProps({ workspaceId, tier, billingInterval }: { workspaceId?: string, tier?: WorkspacePlanTier, billingInterval?: BillingInterval }) {
    const locationProps = getCheckoutPropsFromLocation();

    tier = tier ?? locationProps.tier;
    billingInterval = billingInterval ?? locationProps.billingInterval;

    let priceId: string;

    const requestedPlans = (locationProps.priceIds ?? [])
        .map(id => StripeUtils.Plans.getRecurringPlanData(id))
        .filter(Boolean)
        .map(planData => {
            if (planData.plan.isDeprecated) {
                return StripeUtils.Plans.getRecurringPlanData(planData.plan.migrateTo);
            }
            return planData;
        })
        .filter(Boolean);

    if (requestedPlans.length) {
        let planData = requestedPlans[0];
        if (billingInterval) {
            planData = requestedPlans.find(planData => planData.plan.interval === billingInterval) ?? planData;
        }
        billingInterval = planData.plan.interval;
        tier = planData.tier;
        priceId = planData.plan.planId;
    } else if (tier) {
        if (billingInterval) {
            priceId = StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
        } else {
            billingInterval = "year";
            priceId = StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
        }
    } else {
        tier = WorkspacePlanTier.PRO;
        billingInterval = "year";
        priceId = StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
    }

    const requestedPriceIdsByBillingInterval = requestedPlans.reduce((priceIdsByBillingInterval, planData) => {
        if (planData.tier === tier && !priceIdsByBillingInterval[planData.plan.interval]) {
            priceIdsByBillingInterval[planData.plan.interval] = planData.plan.planId;
        }

        return priceIdsByBillingInterval;
    }, {} as BillingIntervalPriceIds);

    workspaceId = workspaceId ?? locationProps.workspaceId;

    return {
        ..._.omit(locationProps, ["priceIds", "billingInterval"]),
        workspaceId,
        tier,
        priceId,
        requestedPriceIdsByBillingInterval
    };
}

function getUsedSeatCount(users: IWorkspaceUserExtended[], userInvites: IWorkspaceUserInvite[]) {
    return users.filter(user => user.role !== WorkspaceUserRole.GUEST).length +
        userInvites.filter(invite => invite.role !== WorkspaceUserRole.GUEST).length;
}

export type CheckoutType = "reactivate" | "new_with_trial" | "new_no_trial";

export interface CheckoutControllerState {
    initialized: boolean;
    workspace: {
        metadata: IWorkspaceMetadata;
        users: IWorkspaceUserExtended[];
        userInvites: IWorkspaceUserInvite[];
    }
    tier: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM;
    stripeData: GetWorkspaceStripeDataResponse;
    priceId: string;
    productDisplayName: string | null;
    billingAddress: StripeTypes.Address;
    billingAddressValid: boolean;
    taxRate: number;
    minQuantity: number;
    maxQuantity: number;
    quantity: number;
    taxId: Pick<StripeTypes.TaxId, "type" | "value">;
    promo: StripeTypes.PromotionCode;
    fetching: boolean;
    paymentElement: {
        complete: boolean;
    };
    createPaymentMethod: boolean;
    successfulCheckout: boolean;
    /**
     * Checkout session state returned from server, mostly used to ensure server state matches client state
     */
    checkoutSession: Awaited<ReturnType<typeof createCheckoutSession>>["session"] | null;
    /**
     * Stripe elements provider value, hijacked from the provider
     */
    elements: StripeElements | null;
    /**
     * Stripe useCheckoutElements() hook value, hijacked from the hook
     */
    checkoutElements: StripeUseCheckoutElementsResult | null;
    /**
     * Stripe instance, hijacked from the provider
     */
    stripe: Stripe | null;
}

const initialState: CheckoutControllerState = {
    initialized: false,
    workspace: {
        metadata: null,
        users: [],
        userInvites: []
    },
    tier: null,
    stripeData: {
        customer: null,
        subscription: null,
        paymentMethods: {},
        invoices: {},
        upcomingInvoice: null
    },
    priceId: null,
    productDisplayName: null,
    billingAddress: {
        city: "",
        country: "",
        line1: "",
        line2: "",
        postal_code: "",
        state: ""
    },
    paymentElement: {
        complete: false
    },
    billingAddressValid: false,
    taxRate: 0,
    minQuantity: 1,
    maxQuantity: 1,
    quantity: 1,
    taxId: null,
    promo: null,
    createPaymentMethod: false,
    fetching: false,
    successfulCheckout: false,
    checkoutSession: null,
    elements: null,
    checkoutElements: null,
    stripe: null,
};

export interface OnSuccessParams {
    workspaceId: string;
    tier: WorkspacePlanTier;
    interval: BillingInterval;
    priceId: string;
    trialDays: number;
    quantity: number;
}

class CheckoutController extends GlobalStateController<CheckoutControllerState> {
    private _firebaseUser: FirebaseUser;
    private _userModel: IUserStorageModel;
    private _runSequentially: SequentialRunner;
    private _onSuccess: (params: OnSuccessParams) => void;
    private _onCancel?: () => void;
    private _requestedPriceIdsByBillingInterval: BillingIntervalPriceIds = {};

    public get initialized() {
        return this._state.initialized;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    get usedSeatCount() {
        const { users, userInvites } = this._state.workspace;
        return getUsedSeatCount(users, userInvites);
    }

    get customer() {
        const { customer } = this._state.stripeData;
        return customer;
    }

    get state() {
        return this._state;
    }

    protected _getAvailableTrialDays(tier: WorkspacePlanTier) {
        const { workspace: { metadata } } = this._state;

        if (["active", "past_due"].includes(metadata?.subscriptionState?.type)) {
            if (metadata.subscriptionTrialEndsAt && metadata.subscriptionState.subscriptionStatus === "trialing") {
                // Sub is already in trial, return the number of days remaining as it's the sub that will be upgraded
                return Math.ceil((metadata.subscriptionTrialEndsAt - Date.now()) / 1000 / 60 / 60 / 24);
            }

            // Sub is active or past due, but not in trial, no trial will be offered
            return 0;
        }

        if (metadata?.subscriptionState?.type === "terminal") {
            // Has old sub in terminal state, no trial will be offered
            return 0;
        }

        return this._userModel.availableTrialDays?.[tier] ?? 0;
    }

    get availableTrialDays() {
        return this._getAvailableTrialDays(this._state.tier);
    }

    get plansForBillingIntervals() {
        const { tier, priceId } = this._state;

        const { plan } = StripeUtils.Plans.getRecurringPlanData(priceId);
        const getPlan = (billingInterval: BillingInterval) => {
            if (plan.interval === billingInterval) {
                return plan;
            }

            const intervalPriceId = this._requestedPriceIdsByBillingInterval[billingInterval] ??
                StripeUtils.Plans.getDefaultRecurringPlanId(tier, billingInterval);
            return StripeUtils.Plans.getRecurringPlanData(intervalPriceId).plan;
        };

        return {
            month: getPlan("month"),
            year: getPlan("year")
        };
    }

    get plan() {
        const { priceId } = this._state;
        return StripeUtils.Plans.getRecurringPlanData(priceId).plan;
    }

    get billingInterval() {
        const { priceId } = this._state;
        return StripeUtils.Plans.getRecurringPlanInterval(priceId);
    }

    get productDisplayName() {
        const { priceId, productDisplayName } = this._state;
        return productDisplayName ?? StripeUtils.Plans.getRecurringPlanData(priceId).productName;
    }

    get defaultPaymentMethod() {
        const { paymentMethods } = this._state.stripeData;
        return StripeUtils.PaymentMethod.getDefaultPaymentMethod(this._state.stripeData.subscription, Object.values(paymentMethods));
    }

    get currency() {
        const { checkoutElements, checkoutSession } = this._state;

        if (checkoutSession && checkoutElements?.type === "success") {
            return checkoutElements.checkout.currency.toUpperCase();
        }

        return "USD";
    }

    get conversionRate() {
        const { checkoutElements, checkoutSession } = this._state;

        if (checkoutSession && checkoutElements?.type === "success") {
            return checkoutElements.checkout.currencyOptions?.find(option => option.currency === checkoutElements.checkout.currency)?.currencyConversion?.fxRate ?? 1;
        }

        return 1;
    }

    get canCheckout() {
        const { billingAddressValid, paymentElement: { complete }, createPaymentMethod, quantity, maxQuantity, minQuantity, elements, checkoutElements, checkoutSession, priceId, billingAddress, stripe, taxId, promo } = this._state;

        if (!stripe) {
            return false;
        }

        if (!elements && !checkoutElements) {
            return false;
        }

        if (!billingAddressValid) {
            return false;
        }

        if (quantity > maxQuantity || quantity < minQuantity) {
            return false;
        }

        if (quantity < this.usedSeatCount) {
            return false;
        }

        if (checkoutElements) {
            if (checkoutElements.type !== "success") {
                return false;
            }

            const checkout = checkoutElements.checkout;

            if (!checkout.canConfirm) {
                return false;
            }

            // Local checkout session state <> controller state
            if (checkout.lineItems[0]?.quantity !== quantity) {
                return false;
            }

            if ((checkout.billingAddress?.address.country ?? "") !== (billingAddress.country ?? "")) {
                return false;
            }

            if ((checkout.billingAddress?.address.postal_code ?? "") !== (billingAddress.postal_code ?? "")) {
                return false;
            }

            if ((checkout.discountAmounts?.[0]?.promotionCode ?? "") !== (promo?.code ?? "")) {
                return false;
            }

            // Server checkout session state <> controller state
            if (checkoutSession.priceId !== priceId) {
                return false;
            }

            if (checkoutSession.billingAddress.country !== billingAddress.country) {
                return false;
            }

            if (checkoutSession.billingAddress.postal_code !== billingAddress.postal_code) {
                return false;
            }

            if ((checkoutSession.taxId?.value ?? "") !== (taxId?.value ?? "")) {
                return false;
            }

            if (checkoutSession.quantity !== quantity) {
                return false;
            }

            return complete;
        }

        if (createPaymentMethod) {
            return complete;
        }

        return complete || !!this.defaultPaymentMethod;
    }

    get canCancel() {
        return !!this._onCancel;
    }

    get workspaceId() {
        const { workspace: { metadata: { id } } } = this._state;
        return id;
    }

    /**
     * Amounts are in USD.
     */
    get amounts() {
        const { taxRate, promo, quantity, priceId, stripeData: { subscription } } = this._state;

        let total = StripeUtils.calculateStripePriceAmountCents(priceId, quantity) / 100;
        let taxTotal = taxRate * total;
        const promoCoupon = StripeUtils.getCoupon(promo);
        if (promoCoupon) {
            const totalBefore = total;
            if (promoCoupon.amount_off) {
                total = Math.max(total - promoCoupon.amount_off / 100, 0);
            } else {
                total = total - (total / 100) * promoCoupon.percent_off;
            }
            const ratio = totalBefore > 0 ? total / totalBefore : 1;
            taxTotal = taxTotal * ratio;
        }

        const totalWithTax = total + taxTotal;

        const prorationCredit = StripeUtils.Subscription.getProrationCredit(subscription, {
            newTotalCostDollars: total,
        });

        const totalWithProrationCredit = total - prorationCredit;
        const taxTotalWithProrationCredit = totalWithProrationCredit * taxRate;

        let dueNow: number;
        let dueNext: number;
        let dueNowTax: number;
        let dueNextTax: number;
        if (this.availableTrialDays > 0) {
            dueNow = 0;
            dueNowTax = 0;
            dueNext = totalWithProrationCredit;
            dueNextTax = taxTotalWithProrationCredit;
        } else {
            dueNow = totalWithProrationCredit;
            dueNowTax = taxTotalWithProrationCredit;
            dueNext = total;
            dueNextTax = taxTotal;
        }

        const { month, year } = this.plansForBillingIntervals;

        const annualCostAtMonthlyRate = (month?.amount ?? 0) * 12 / 100;
        const annualCost = (year?.amount ?? 0) / 100;
        const annualSavingsPercentage = (1 - annualCost / annualCostAtMonthlyRate) * 100;
        const monthlyCost = (month?.amount ?? 0) / 100;

        const amounts = {
            taxTotal,
            total,
            totalWithTax,
            dueNow,
            dueNowTax,
            dueNext,
            dueNextTax,
            annualSavingsPercentage,
            annualCostAtMonthlyRate,
            annualCost,
            monthlyCost,
            prorationCredit
        };

        Object.entries(amounts).forEach(([key, value]) => {
            if (key === "annualSavingsPercentage") {
                return;
            }
            amounts[key] = value * this.conversionRate;
        });

        return amounts;
    }

    get checkoutType(): CheckoutType {
        const { workspace: { metadata } } = this._state;
        if (metadata.subscriptionCanceledAt) {
            return "reactivate";
        } else if (this.availableTrialDays > 0) {
            return "new_with_trial";
        } else {
            return "new_no_trial";
        }
    }

    get labels() {
        const { workspace: { metadata: { name } } } = this._state;

        const checkoutType = this.checkoutType;
        const productDisplayName = this.productDisplayName;

        const disclaimer = t("By providing your card information, you allow Beautiful.ai to charge your card for future payments in accordance with their terms.");
        const cancelLabel = t("Cancel");
        const progressMessage = t("This will only take a moment...");

        switch (checkoutType) {
            case "reactivate": {
                return {
                    title: t("Reactivate \"{{name}}\" {{productDisplayName}} subscription", { name, productDisplayName }),
                    subtitle: null,
                    submitLabel: t("Reactivate"),
                    progressTitle: t("Reactivating subscription"),
                    progressMessage,
                    cancelLabel,
                    disclaimer,
                };
            }
            case "new_no_trial":
                return {
                    title: t("Upgrade to Beautiful.ai {{productDisplayName}}", { productDisplayName }),
                    subtitle: null,
                    submitLabel: t("Upgrade"),
                    progressTitle: t("Upgrading plan"),
                    progressMessage,
                    cancelLabel,
                    disclaimer,
                };
            case "new_with_trial":
                return {
                    title: t("Start your {{days}} day free trial", { days: this.availableTrialDays }),
                    subtitle: t("Cancel before your trial ends and you won't be charged."),
                    submitLabel: t("Start free trial"),
                    progressTitle: t("Starting free trial"),
                    progressMessage,
                    cancelLabel,
                    disclaimer,
                };
        }
    }

    /**
     * High level wrapper that must be used around the root component.
     * IMPORTANT: checkout session injection is not implemented yet
     */
    public withStripeElementsAndSession<T extends React.ComponentType<any>>(Component: T, options: { appearance?: Appearance, appearanceVariant?: "A" | "B" }) {
        type InjectedProps = { stripe: Stripe, elements?: StripeElements, checkoutElements?: StripeUseCheckoutElementsResult };

        const controller = this;

        function WrappedComponent(props: InjectedProps & CheckoutControllerState & React.ComponentProps<T>, ref: React.Ref<T>) {
            const { stripe, elements, checkoutElements } = props;

            // Hijacking the provider's state and silently assigning it to the controller state,
            // we don't need to call updateState() because Stripe providers have already
            // triggered the root component refresh
            Object.assign(controller._state, { stripe, elements, checkoutElements });

            // @ts-ignore
            return <Component {...props} ref={ref} />;
        }

        const ForwardedWrappedComponent = forwardRef(WrappedComponent);

        const ComponentWithCheckoutSession = withCheckoutSession(ForwardedWrappedComponent, options);
        const ComponentWithElements = withElements(ForwardedWrappedComponent, options);

        function Wrapper(props: CheckoutControllerState & Omit<React.ComponentProps<T>, keyof InjectedProps> & Partial<InjectedProps> & { stripe: Stripe }, ref: React.Ref<T>) {
            const { initialized, checkoutSession } = props;

            if (!initialized) {
                return <Spinner />;
            }

            const fullProps = props as InjectedProps & CheckoutControllerState & React.ComponentProps<T>;

            if (checkoutSession) {
                return <ComponentWithCheckoutSession {...fullProps} clientSecret={checkoutSession.clientSecret} ref={ref} />;
            }

            return <ComponentWithElements {...fullProps} ref={ref} />;
        }

        return this.withState(withStripe(forwardRef(Wrapper)));
    }

    public async initialize({
        workspaceId: requestedWorkspaceId,
        tier: requestedTier,
        billingInterval: requestedBillingInterval,
        createPaymentMethod = false,
        onSuccess,
        onCancel
    }: {
        workspaceId?: string;
        tier?: WorkspacePlanTier;
        billingInterval?: BillingInterval;
        createPaymentMethod?: boolean;
        onSuccess: (params: OnSuccessParams) => void;
        onCancel?: () => void;
    }) {
        await this._runSequentially(async () => {
            logger.info("[CheckoutController] initialize()");

            await this._reset();

            ux.checkout.register({ tier: requestedTier, workspaceId: requestedWorkspaceId });

            this._firebaseUser = auth().currentUser;

            if (AppController.initialized && AppController.user) {
                this._userModel = AppController.user;
            } else {
                this._userModel = new User({ id: this._firebaseUser.uid }, { autoLoad: false });
                await this._userModel.load();
            }

            let {
                workspaceId,
                tier,
                priceId,
                displayName,
                promotionCode,
                minQuantity,
                requestedPriceIdsByBillingInterval
            } = await getInitialProps({ workspaceId: requestedWorkspaceId, tier: requestedTier, billingInterval: requestedBillingInterval });

            if (!workspaceId) {
                const selectOwnedWorkspaceId = () => {
                    if (tier === WorkspacePlanTier.PRO) {
                        const defaultWorkspaceId = this._userModel.getDefaultWorkspaceMetadata()?.id;
                        if (defaultWorkspaceId) {
                            logger.info("[CheckoutController] selected default basic workspace", { userId: this._userModel.id, workspaceId: defaultWorkspaceId });
                            return defaultWorkspaceId;
                        }
                    }

                    // Just gating by owner here instead of actual permissions
                    const initialWorkspaceId = getInitialWorkspaceId(this._userModel.workspacesMetadata, this._userModel.get("recentWorkspaceId"), [WorkspaceUserRole.OWNER]);
                    if (initialWorkspaceId) {
                        logger.info("[CheckoutController] selected initial workspace", { userId: this._userModel.id, workspaceId: initialWorkspaceId });
                    }
                    return initialWorkspaceId;
                };

                workspaceId = selectOwnedWorkspaceId();
                if (!workspaceId) {
                    await this._userModel.loadWorkspacesMetadata();
                    workspaceId = selectOwnedWorkspaceId();
                }
                if (!workspaceId) {
                    throw new Error(t("No workspace found"));
                }
            } else {
                logger.info("[CheckoutController] selected requested workspace", { userId: this._userModel.id, workspaceId });
            }

            if (workspaceId !== requestedWorkspaceId || tier !== requestedTier) {
                ux.checkout.update({ workspaceId, tier });
            }

            const workspaceMetadata = this._userModel.workspacesMetadata.find(metadata => metadata.id === workspaceId);
            if (!workspaceMetadata) {
                throw new Error("Workspace not found");
            }

            const workspaceActionPermissions = await getActionPermissionsForWorkspace(workspaceId);
            if (!workspaceActionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE]) {
                logger.warn("[CheckoutController] initialize() billing permission denied", {
                    workspaceId,
                    userRole: workspaceMetadata.userRole,
                    currentPlanTier: workspaceMetadata.plan?.tier,
                    targetTier: tier
                });
                throw new Error("User doesn't have permission to manage billing");
            }

            switch (tier) {
                case WorkspacePlanTier.PRO:
                    // Allow upgrading from PRO to PRO if there is no subscription
                    if (workspaceMetadata.plan.tier !== WorkspacePlanTier.BASIC && !(workspaceMetadata.plan.tier === WorkspacePlanTier.PRO && !workspaceMetadata.plan.requiresSubscription)) {
                        throw new AlreadyPayingForPlanError(t("You are already paying for a {{planName}} plan.", { planName: workspaceMetadata.plan.name }));
                    }
                    break;
                case WorkspacePlanTier.TEAM:
                    if (![WorkspacePlanTier.BASIC, WorkspacePlanTier.PRO].includes(workspaceMetadata.plan.tier)) {
                        throw new AlreadyPayingForPlanError(t("You are already paying for a {{planName}} plan.", { planName: workspaceMetadata.plan.name }));
                    }
                    break;
                case WorkspacePlanTier.ENTERPRISE:
                    throw new Error("Enterprise checkout is not supported");
                default:
                    throw new Error("Invalid target tier");
            }

            const [stripeData, workspaceUsers, userInvites] = await Promise.all([
                getWorkspaceStripeData(workspaceId),
                getWorkspaceUsers(workspaceId),
                getWorkspaceUserInvites(workspaceId)
            ]);

            const defaultPaymentMethod = StripeUtils.PaymentMethod.getDefaultPaymentMethod(stripeData.subscription, Object.values(stripeData.paymentMethods));
            const billingAddress = StripeUtils.Customer.getBillingAddress(stripeData.customer, defaultPaymentMethod);

            let taxId: Pick<StripeTypes.TaxId, "type" | "value"> = null;
            const taxIdValue = StripeUtils.Customer.getTaxId(stripeData.customer);
            if (taxIdValue) {
                taxId = {
                    type: StripeUtils.Customer.getTaxIdType(stripeData.customer),
                    value: taxIdValue
                };
            }

            const usedSeatCount = getUsedSeatCount(workspaceUsers, userInvites);
            minQuantity = StripeUtils.Plans.applyTieredPricingSeatFloor(priceId, minQuantity ?? 1, usedSeatCount);
            let quantity = minQuantity;

            const maxQuantity = MAX_QUANTITIES[tier];

            let checkoutSession: CheckoutControllerState["checkoutSession"] = null;
            // Hard limitation: no sub or terminal sub and no payment method
            if (!defaultPaymentMethod && (!workspaceMetadata.subscriptionState || workspaceMetadata.subscriptionState?.type === "terminal")) {
                const { session } = await createCheckoutSession({ workspaceId, priceId, quantity, billingAddress, taxId });
                checkoutSession = session;
                quantity = session.quantity;
            }

            this._onSuccess = onSuccess;
            this._onCancel = onCancel;
            this._requestedPriceIdsByBillingInterval = requestedPriceIdsByBillingInterval;

            await this._updateState({
                initialized: true,
                minQuantity,
                quantity,
                maxQuantity,
                workspace: {
                    metadata: workspaceMetadata,
                    users: workspaceUsers,
                    userInvites: userInvites
                },
                tier,
                stripeData,
                priceId,
                billingAddress,
                billingAddressValid: true,
                taxId,
                productDisplayName: displayName,
                createPaymentMethod,
                checkoutSession
            });

            try {
                await Analytics.util.identify(this._firebaseUser);
                const analyticsProps = {
                    current_status: workspaceMetadata.plan.tier,
                    workspace_id: workspaceId,
                    price_id: priceId,
                    min_quantity: quantity,
                    ...(displayName && { plan_display_name: displayName })
                };

                if (tier === WorkspacePlanTier.PRO) {
                    trackActivity("Upgrade", "BillingIntentStart", null, null, analyticsProps, { audit: true });
                }
                if (tier === WorkspacePlanTier.TEAM) {
                    trackActivity("Organization", "BillingIntentStart", null, null, analyticsProps, { audit: true });
                }
            } catch (err) {
                logger.error(err, "[CheckoutController] failed to send BillingIntentStart analytics event");
            }

            if (promotionCode) {
                const { promo } = await this._applyPromoCode(promotionCode, priceId);
                if (promo) {
                    await this._updateState({ promo });
                }
            }
        });
    }

    public async reset() {
        return this._runSequentially(() => this._reset());
    }

    public async applyPromoCode(promoCode: string) {
        return this._runSequentially(async () => {
            const { priceId } = this._state;

            const { promo, error } = await this._applyPromoCode(promoCode, priceId);
            if (error) {
                return error;
            }

            await this._updateState({ promo });
            return null;
        });
    }

    public async setBillingInterval(billingInterval: "month" | "year") {
        return this._runSequentially(async () => {
            const { month, year } = this.plansForBillingIntervals;

            const priceId = billingInterval === "year" ? year.planId : month.planId;

            await this._updateState({ priceId });
        });
    }

    public async setBillingAddress(billingAddress: StripeTypes.Address, billingAddressValid: boolean) {
        return this._runSequentially(async () => {
            await this._updateState({ billingAddress, billingAddressValid });
        });
    }

    public async setPaymentElementState({ complete }: { complete: boolean }) {
        return this._runSequentially(async () => {
            await this._updateState({ paymentElement: { complete } });
        });
    }

    public async setCreatePaymentMethod(createPaymentMethod: boolean) {
        return this._runSequentially(async () => {
            await this._updateState({
                createPaymentMethod,
                paymentElement: { complete: false }
            });
        });
    }

    public async setQuantity(quantity: number) {
        return this._runSequentially(async () => {
            await this._updateState({ quantity });
        });
    }

    public async checkout() {
        return this._runSequentially(async () => {
            const {
                workspace: { metadata: { id: workspaceId } },
                quantity,
                tier,
                priceId,
                billingAddress,
                taxId,
                createPaymentMethod,
                promo,
                paymentElement,
                elements,
                stripe,
                checkoutElements,
                checkoutSession
            } = this._state;

            if (!this.canCheckout) {
                throw new Error("Not ready");
            }

            await this._updateState({ fetching: true });

            const availableTrialDays = this.availableTrialDays;

            const checkoutWithSession = async () => {
                if (checkoutElements.type !== "success") {
                    throw new Error("Checkout session is not ready");
                }

                const result = await checkoutElements.checkout.confirm({
                    returnUrl: window.location.href,
                    redirect: "if_required"
                });
                if (result.type === "error") {
                    throw new CheckoutSessionConfirmationError(result.error.message, result.error.code);
                }

                const getSubscriptionId = (model: IUserStorageModel) => {
                    const finalizedCheckoutSessions = model.get("finalizedCheckoutSessions");
                    if (finalizedCheckoutSessions?.[workspaceId]?.sessionId && finalizedCheckoutSessions[workspaceId].sessionId === checkoutSession.id) {
                        return finalizedCheckoutSessions[workspaceId].subscriptionId;
                    }
                    return null;
                };

                // Finalization will happen upon receiving a Stripe webhook, so we're waiting for server to handle that
                // Using firebase real time events instead of Pusher as it's more reliable
                const subscriptionId = await new Promise<string>((resolve, reject) => {
                    let resolved = false;

                    const timeout = setTimeout(() => {
                        if (resolved) {
                            return;
                        }

                        this._userModel.off("change:finalizedCheckoutSessions", onChange);

                        (async () => {
                            // Try explicitly reloading user model first
                            try {
                                await this._userModel.reload();
                                const subscriptionId = getSubscriptionId(this._userModel);
                                if (subscriptionId) {
                                    resolve(subscriptionId);
                                    return;
                                }
                            } catch (err) {
                                logger.error(err, "[CheckoutController] failed to reload user model to get subscription id");
                            }

                            reject(new CheckoutSessionFinalizationTimeoutError("Checkout session finalization timed out"));
                        })();
                    }, 60 * 1000);

                    const onChange = (model: IUserStorageModel) => {
                        const subscriptionId = getSubscriptionId(model);
                        if (subscriptionId) {
                            this._userModel.off("change:finalizedCheckoutSessions", onChange);
                            resolved = true;
                            clearTimeout(timeout);
                            resolve(subscriptionId);
                        }
                    };
                    this._userModel.on("change:finalizedCheckoutSessions", onChange);
                    // Trigger initial check
                    onChange(this._userModel);
                });

                await handleSuccessfulWorkspacePlanUpgrade({
                    workspaceId,
                    subscriptionId,
                    tier,
                    promotionCode: promo?.code,
                    priceId,
                    quantity
                });
            };

            const checkoutWithElements = async () => {
                let paymentMethodWasCreated = false;
                let paymentMethod: StripeTypes.PaymentMethod | null = null;
                if (paymentElement.complete || createPaymentMethod) {
                    try {
                        logger.info("[CheckoutController] creating payment method", { workspaceId });
                        paymentMethod = await this._createPaymentMethod({ stripe, elements });
                        paymentMethodWasCreated = true;
                    } catch (err) {
                        logger.error(err, "[CheckoutController] checkout() failed to create payment method");
                        throw err;
                    }
                } else {
                    logger.info("[CheckoutController] using default payment method", { workspaceId, paymentMethodId: this.defaultPaymentMethod?.id });
                    paymentMethod = this.defaultPaymentMethod;
                }

                logger.info("[CheckoutController] ensuring customer and payment method", { workspaceId, paymentMethodId: paymentMethod?.id });
                await ensureCustomerAndPaymentMethod({
                    workspaceId,
                    billingDetails: {
                        address: billingAddress,
                        taxId: taxId ?? undefined
                    },
                    paymentMethodId: paymentMethod.id
                });

                if (availableTrialDays > 0 && paymentMethodWasCreated) {
                    logger.info("[CheckoutController] checking payment method with stripe radar", { workspaceId });
                    await stripeRadarPaymentCheck({
                        elements,
                        paymentMethod,
                        workspaceId,
                    });
                }

                logger.info("[CheckoutController] upgrading workspace plan", { workspaceId, quantity, tier, priceId });
                await upgradeWorkspacePlan({
                    workspaceId,
                    quantity,
                    tier,
                    priceId,
                    billingDetails: {
                        address: billingAddress,
                        taxId: taxId ?? undefined
                    },
                    paymentMethodId: paymentMethod.id,
                    promotionCode: promo?.code
                });
            };

            try {
                if (checkoutSession) {
                    await checkoutWithSession();
                } else {
                    await checkoutWithElements();
                }

                await this._reloadWorkspaceAndStripeData().catch(err => {
                    logger.error(err, "[CheckoutController] checkout() failed to reload workspace and stripe data after successful checkout");
                });

                await this._updateState({ successfulCheckout: true, fetching: false });

                ux.checkout.complete();

                this._onSuccess({
                    workspaceId,
                    tier,
                    interval: this.billingInterval,
                    trialDays: availableTrialDays,
                    quantity,
                    priceId
                });
            } catch (err) {
                if (!(err instanceof PaymentRequiredError || err instanceof CheckoutSessionConfirmationError || err instanceof StripeRadarError)) {
                    // Only fail if it's not a payment-related error (card declined, etc.)
                    ux.checkout.fail({ error: err }, undefined, true);
                }

                await this._reloadWorkspaceAndStripeData().catch(err => {
                    logger.error(err, "[CheckoutController] checkout() failed to reload workspace and stripe data after failed checkout");
                });

                await this._updateState({ fetching: false });

                throw err;
            }
        });
    }

    public async cancel() {
        return this._runSequentially(async () => {
            this._onCancel();

            await this._reset();
        });
    }

    protected async _reloadWorkspaceAndStripeData() {
        let metadata: IWorkspaceMetadata;
        let stripeData: GetWorkspaceStripeDataResponse;
        await Promise.all([
            (async () => {
                await this._userModel.loadWorkspacesMetadata([this.workspaceId]);
                metadata = this._userModel.workspacesMetadata.find(metadata => metadata.id === this.workspaceId);
            })(),
            (async () => {
                stripeData = await getWorkspaceStripeData(this.workspaceId);
            })()
        ]);

        await this._updateState(state => ({
            ...state,
            workspace: {
                ...state.workspace,
                metadata
            },
            stripeData
        }));
    }

    protected async _createPaymentMethod({ stripe, elements }: { stripe: Stripe, elements: StripeElements }) {
        const { billingAddress } = this._state;

        const { error } = await elements.submit();
        if (error) {
            throw new Error(error.message);
        }

        const { paymentMethod } = await handleStripeErrors<PaymentMethodResult>(() => stripe.createPaymentMethod({
            elements,
            params: {
                billing_details: {
                    address: billingAddress
                }
            }
        }));

        return paymentMethod as unknown as StripeTypes.PaymentMethod;
    }

    protected async _applyPromoCode(promoCode: string, priceId: string): Promise<{ promo: StripeTypes.PromotionCode, error: string }> {
        try {
            const validationError = validatePromoCode(promoCode);
            if (validationError) {
                return {
                    promo: null,
                    error: validationError
                };
            }

            const promotionCode = await getPromotionCode(promoCode);

            const usageError = this._canUsePromotionCode(promotionCode, priceId);
            if (usageError) {
                return {
                    promo: null,
                    error: usageError
                };
            }

            return {
                promo: promotionCode,
                error: null
            };
        } catch (err) {
            if (err instanceof InternalServerError) {
                logger.error(err, "[CheckoutController] _applyPromoCode() failed to get promotion code");
                throw err;
            }

            return {
                promo: null,
                error: err.message
            };
        }
    }

    protected _canUsePromotionCode(promo: StripeTypes.PromotionCode, priceId: string) {
        const planData = StripeUtils.Plans.getRecurringPlanData(priceId);

        const productPrice = planData.plan.amount;
        const productName = planData.productName;
        const productId = planData.productId;

        const hasTrial = this._getAvailableTrialDays(planData.tier) > 0;

        const coupon = StripeUtils.getCoupon(promo);
        if (coupon?.applies_to?.products && !coupon.applies_to.products.includes(productId)) {
            return t("This coupon can't be applied to a {{productName}} plan", { productName });
        }

        if (promo.restrictions?.minimum_amount && productPrice < promo.restrictions.minimum_amount) {
            return t("This promo can't be applied to bills of less than ${{amount}}", { amount: promo.restrictions.minimum_amount / 100 });
        }

        if (promo.restrictions?.minimum_amount && hasTrial) {
            return t("This promo can't be applied until after your free trial is over");
        }

        if (promo.restrictions?.first_time_transaction && !hasTrial) {
            return t("This promo is only for new users");
        }

        return null;
    }

    protected _stateDidUpdate(prevState: CheckoutControllerState) {
        this._runSequentially(async () => {
            const { billingAddress, billingAddressValid, taxId, priceId, initialized, promo, checkoutSession, quantity, minQuantity, maxQuantity, checkoutElements, workspace } = this._state;

            if (!initialized) {
                return;
            }

            if (
                getObjectHash(_.pick(billingAddress, ["country", "postal_code"])) !== getObjectHash(_.pick(prevState.billingAddress, ["country", "postal_code"])) ||
                (taxId?.value ?? "") !== (prevState.taxId?.value ?? "") ||
                priceId !== prevState.priceId
            ) {
                await this._calculateTax();
            }

            if (priceId !== prevState.priceId) {
                if (promo) {
                    const usageError = this._canUsePromotionCode(promo, priceId);
                    if (usageError) {
                        await this._updateState({ promo: null });
                    }
                }
            }

            if (checkoutSession && checkoutElements?.type === "success") {
                const quantityValid = quantity >= minQuantity && quantity <= maxQuantity;
                if (!quantityValid || !billingAddressValid) {
                    return;
                }

                if (getObjectHash(checkoutElements.checkout.billingAddress?.address ?? {}) !== getObjectHash(billingAddress ?? {})) {
                    const result = await checkoutElements.checkout.updateBillingAddress({
                        address: billingAddress,
                        name: workspace.metadata.name
                    });
                    if (result.type === "error") {
                        logger.error(new Error(result.error.message), "[CheckoutController] _stateDidUpdate() failed to update checkout session billing address");
                    }
                }

                if ((checkoutElements.checkout.taxIdInfo?.taxId?.value ?? "") !== (taxId?.value ?? "")) {
                    const result = await checkoutElements.checkout.updateTaxIdInfo({
                        // @ts-ignore
                        taxId: taxId?.type ? { type: taxId.type, value: taxId.value } : null,
                        businessName: workspace.metadata.name
                    });
                    if (result.type === "error") {
                        logger.error(new Error(result.error.message), "[CheckoutController] _stateDidUpdate() failed to update checkout session tax id info");
                    }
                }

                if ((checkoutElements.checkout.discountAmounts?.[0]?.promotionCode ?? "") !== (promo?.code ?? "")) {
                    const result = promo?.code ? await checkoutElements.checkout.applyPromotionCode(promo.code) : await checkoutElements.checkout.removePromotionCode();
                    if (result.type === "error") {
                        logger.error(new Error(result.error.message), "[CheckoutController] _stateDidUpdate() failed to update checkout session promo code");
                    } else {
                        // update promo state so we can run canCheckout() against the right promo code
                        await this._updateState({ promo });
                    }
                }

                if (
                    checkoutSession.priceId !== priceId ||
                    checkoutSession.quantity !== quantity ||
                    checkoutSession.billingAddress.country !== billingAddress.country ||
                    checkoutSession.billingAddress.postal_code !== billingAddress.postal_code ||
                    (checkoutSession.taxId?.value ?? "") !== (taxId?.value ?? "")
                ) {
                    const result = await checkoutElements.checkout.runServerUpdate(async () => {
                        const { session } = await updateCheckoutSession({
                            workspaceId: this.workspaceId,
                            sessionId: checkoutSession.id,
                            priceId,
                            quantity,
                            billingAddress,
                            taxId
                        });

                        await this._updateState({ checkoutSession: session, quantity: session.quantity });
                    });
                    if (result.type === "error") {
                        logger.error(new Error(result.error.message), "[CheckoutController] _stateDidUpdate() failed to update checkout session");
                    }
                }
            }
        }).catch(error => {
            logger.error(error, "[CheckoutController] _stateDidUpdate() failed");

            // Assume failed and started over
            ux.checkout.fail({ error }, undefined, true);
        });
    }

    protected async _reset() {
        logger.info("[CheckoutController] reset()");

        ux.checkout.abandon();

        this._firebaseUser = null;
        this._userModel = null;
        this._onSuccess = null;
        this._onCancel = null;
        this._requestedPriceIdsByBillingInterval = {};

        await this._updateState(_.cloneDeep(initialState));
    }

    protected async _calculateTax() {
        const { billingAddress, billingAddressValid, taxId, tier } = this._state;

        if (!billingAddressValid) {
            await this._updateState({ taxRate: 0 });
            return;
        }

        try {
            const { taxRate } = await calculateTaxAmount({
                amount: this.plan.amount,
                stripeProductId: stripeProducts[tier].productId,
                countryCode: billingAddress.country,
                postalCode: billingAddress.postal_code,
                taxId: taxId?.value
            });

            await this._updateState({ taxRate });
        } catch (err) {
            logger.error(err, "[CheckoutController] _calculateTax() failed");

            await this._updateState({ taxRate: 0 });
        }
    }
}

const checkoutController = new CheckoutController();

// for lazy import and debug
app.checkoutController = checkoutController;

export default checkoutController;

export const CheckoutControllerInitializer = checkoutController.withState(function CheckoutControllerInitializer({
    children,
    workspaceId,
    tier,
    billingInterval,
    createPaymentMethod,
    onSuccess,
    onCancel,
    initialized
}: {
    children: React.ReactNode | ((state: CheckoutControllerState) => React.ReactElement),
    workspaceId?: string,
    tier?: WorkspacePlanTier,
    billingInterval?: BillingInterval,
    createPaymentMethod?: boolean,
    onSuccess: (params: OnSuccessParams) => void,
    onCancel?: () => void,
    initialized: boolean
}) {
    useEffect(() => {
        checkoutController.initialize({
            workspaceId,
            tier,
            billingInterval,
            createPaymentMethod,
            onSuccess,
            onCancel
        }).catch(err => {
            logger.error(err, "[CheckoutController] initialize() failed");

            if (err instanceof AlreadyPayingForPlanError) {
                ux.checkout.abandon();
            } else {
                ux.checkout.fail({ error: err });
            }

            ShowErrorDialog({
                title: t("Oops, something went wrong"),
                message: `${err.message} ${t("Let's take you to your presentation library.")}`,
                onClose: () => window.location.href = "/"
            });
        });

        return () => {
            checkoutController.reset();
        };
    }, []);

    if (initialized) {
        return typeof children === "function" ? children(checkoutController.state) : children;
    }

    return <Spinner />;
});
